import { useMemo, useState } from "react";
import CustomSharedTitleSection from "sipli-fleet-core/components/CustomSharedTitleSection/CustomSharedTitleSection";
import { useQueryGetTireMakesList } from "src/queries/administrator/tireMake.queries";
import type { TireMakeRecord } from "src/services/administrator/tireMakeService";
import { hasAdministrationPermission } from "src/util/administrationPermission";
import TireMakeFormModal from "./TireMakeFormModal";
import TireMakesTable from "./TireMakesTable";
import "../../Customers/Customers.css";

const DEFAULT_PAGINATION = {
  page: 0,
  size: 10,
};

interface TireMakeSorting {
  sortBy: string | undefined;
  sortDir: "ASC" | "DESC" | undefined;
}

const TireMakes = () => {
  const [pagination, setPagination] = useState(DEFAULT_PAGINATION);
  const [sorting, setSorting] = useState<TireMakeSorting>({
    sortBy: "ID",
    sortDir: "DESC",
  });
  const [search, setSearch] = useState("");
  const [formRecord, setFormRecord] = useState<TireMakeRecord | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const canEdit = hasAdministrationPermission("edit");

  const queryParams = useMemo(
    () => ({
      page: pagination.page,
      size: pagination.size,
      sort: sorting.sortDir ?? "DESC",
      sortBy: sorting.sortBy ?? "ID",
    }),
    [pagination.page, pagination.size, sorting.sortBy, sorting.sortDir]
  );

  const { data, isLoading } = useQueryGetTireMakesList(queryParams, {
    enabled: true,
  });

  const response = data?.data?.response;
  const records = useMemo(
    () =>
      response?.resources ??
      response?.tireMakes ??
      response?.data ??
      response?.items ??
      response?.content ??
      [],
    [response]
  );

  const filteredRecords = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();

    if (!normalizedSearch) return records;

    return records.filter((record) =>
      [record.name, record.description, record.active ? "active" : "inactive"]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(normalizedSearch)
    );
  }, [records, search]);

  const totalCount = search.trim()
    ? filteredRecords.length
    : (response?.totalItems ?? response?.totalElements ?? response?.total ?? records.length);

  const openCreateForm = () => {
    setFormRecord(null);
    setIsFormOpen(true);
  };

  const openEditForm = (record: TireMakeRecord) => {
    setFormRecord(record);
    setIsFormOpen(true);
  };

  return (
    <section className="pb-8 pl-2 bg-gray1">
      <CustomSharedTitleSection
        titleKey="tire_make"
        buttonKey="create"
        onClick={openCreateForm}
        showBtn={canEdit}
        buttonProps={{
          className: "!text-gray2 !w-[94px] !h-[40px]",
        }}
        titleClassName="!text-[25.23px] !font-[700]"
      />

      <div className="flex flex-col overflow-hidden bg-white rounded-[16px]">
        <TireMakesTable
          data={filteredRecords}
          isLoading={isLoading}
          pagination={{ ...pagination, total: totalCount }}
          setPagination={setPagination}
          sorting={sorting}
          setSorting={setSorting}
          search={search}
          setSearch={setSearch}
          onEdit={openEditForm}
        />
      </div>

      <TireMakeFormModal
        open={isFormOpen}
        record={formRecord}
        onClose={() => setIsFormOpen(false)}
      />
    </section>
  );
};

export default TireMakes;
